import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, ArrowRight, AlertCircle, AlertTriangle, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import CallbackHelp from "@/components/CallbackHelp";
import { getPageConfig } from "@/services/builderService";
import type { PageConfig } from "@/services/builderService";
import componentRegistry, { resolveRegistryEntry } from "@/registry/componentRegistry";
import { orchestratorBatch, logJourneyEvent, getJourneyState } from "@/apps/los/services/losService";
import axios from "axios";
import BrandedJourneyHeader from "@/components/BrandedJourneyHeader";
import SegmentedProgressBar from "@/apps/los/components/ProgressBar/SegmentedProgressBar";
import JourneySectionProgress, { JourneyProgressSection } from "@/apps/los/components/ProgressBar/JourneySectionProgress";
import { useCaprionStatusPolling } from "@/hooks/useCaprionStatusPolling";
import CaprionStatusLoader from "@/components/LOS/Caprion/CaprionStatusLoader";
import ChangeBankDetailsLink from "@/apps/los/components/ChangeBankDetailsLink/ChangeBankDetailsLink";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

/**
 * JourneyPage — Single-URL Dynamic Journey Renderer
 *
 * This component powers the `/loan-application` route. It renders the current
 * journey page based entirely on the backend's journey state. The URL never
 * changes — the backend decides which page/components to show.
 *
 * Flow:
 * 1. On mount, fetch journey state from backend → get current page ID
 * 2. Fetch page config for that page ID → get component list
 * 3. Prefill component data via orchestrator batch
 * 4. On "Continue", save data, advance journey, re-fetch new page config
 *
 * Data-flow for prefill:
 * 1. Fetch page config → get list of component IDs
 * 2. Deduplicate getter IDs (multiple components can share the same getter)
 * 3. Call orchestratorBatch once with the unique getter IDs
 * 4. Distribute data to each component via localState
 *
 * Save-flow (Next button):
 * 1. Collect setter IDs from the registry for each component
 * 2. Filter fields per component's whitelist
 * 3. Merge payloads for shared setters
 * 4. Call orchestratorBatch with merged payload
 * 5. Advance journey → backend returns new page ID
 * 6. Re-render with new page config (no navigation)
 *
 * Auto-fire pages (autoFire: true in registry metadata):
 * - The Continue button is hidden.
 * - JourneyPage passes an `onDone` callback to the component.
 * - When the component calls `onDone()`, JourneyPage re-fetches journey state
 *   and renders the next page — no navigation, no double-mount.
 */
export default function JourneyPage() {
    const navigate = useNavigate();
    const { toast } = useToast();
    
    const [pageConfig, setPageConfig] = useState<PageConfig | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [localState, setLocalState] = useState<Record<string, any>>({});
    // Gate field errors: keyed by field name (e.g. "first_name", "date_of_birth", "gender", "individual_pan_number")
    // Cleared when the user edits the field. Shown inline below the relevant field.
    const [gateFieldErrors, setGateFieldErrors] = useState<Record<string, string>>({});
    const [currentPageId, setCurrentPageId] = useState<number | null>(null);
    // Either-mandate: tracks whether UPI was skipped so NACH section unlocks on the same page
    const [upiSkipped, setUpiSkipped] = useState(false);
    // eMandate page: lender-side save failure. Held in state (not just a toast) so the
    // reason stays on screen while the failed mandate is rolled back and its card is
    // unlocked for a retry — a toast alone disappears before the user has read it.
    const [mandateSaveError, setMandateSaveError] = useState<{
        message: string;
        /** 'nach' | 'upi' — which card is being unlocked, if any */
        retry: string | null;
        /** Page component id of that card, so we can tell when the retry succeeds */
        componentId: string | null;
        /** True while the unlock delay is still running */
        unlocking: boolean;
    } | null>(null);
    // Aadhaar page: lender-side SaveAadhaarDetails failure (e.g. Caprion code 32,
    // Aadhaar name not matching the applicant details on record). A plain boolean —
    // the lender's own message is NOT stored or shown, because it names our vendor
    // and its XML format and gives the applicant nothing to act on; the banner shows
    // fixed guidance instead. The real reason stays in the logs and in
    // CaprionRequestResponse for support.
    //
    // Held in state rather than a toast because the usual cause is a name/DOB
    // mismatch the user cannot fix by retrying — the message has to STAY on screen
    // long enough to read and reach support.
    const [aadhaarSaveError, setAadhaarSaveError] = useState(false);
    // Contact-support dialog for the Aadhaar banner above.
    const [aadhaarSupportOpen, setAadhaarSupportOpen] = useState(false);
    // Journey progress — derived from checkpoint_template
    const [journeyCurrentStep, setJourneyCurrentStep] = useState<number>(1);
    const [journeyTotalSteps, setJourneyTotalSteps] = useState<number>(1);
    // Grouped progress sections (backend: progress_sections, built from each page's
    // layout.progress_section). Empty → header falls back to SegmentedProgressBar,
    // so journeys whose pages have no sections configured are unaffected.
    const [progressSections, setProgressSections] = useState<JourneyProgressSection[]>([]);
    // Ref mirror of currentPageId so fetchCurrentPage (stable closure) can read the latest value
    const currentPageIdRef = useRef<number | null>(null);
    // Track BAP name for conditional header rendering
    const [bapName, setBapName] = useState<string | null>(null);
    
    // Allow components to register custom async submit handlers
    const submitHandlersRef = useRef<Record<string, () => Promise<boolean>>>({});

    // Guard: prevent concurrent fetchCurrentPage calls (e.g. onDone firing while already loading)
    const isFetchingRef = useRef(false);

    // Stable refs so callbacks don't change identity on every render
    const navigateRef = useRef(navigate);
    useEffect(() => { navigateRef.current = navigate; }, [navigate]);

    // Caprion status polling hook
    const { startPolling, isPolling, currentStep: pollingStep, error: pollingError } = useCaprionStatusPolling();

    // Helper: Load page config and prefill component data
    // Stable — no deps that change on render
    const loadPageConfig = useCallback(async (config: PageConfig) => {
        setPageConfig(config);
        setCurrentPageId(config.id);
        currentPageIdRef.current = config.id;
        // Reset submit handlers for new page
        submitHandlersRef.current = {};
        // Reset either-mandate UPI-skipped flag when navigating to a new page
        setUpiSkipped(false);
        // Clear any mandate-save error from a previous page
        setMandateSaveError(null);

        // Clear auto-fire sessionStorage guards for the NEW page so the component
        // fires correctly when the user legitimately arrives at this page for the
        // first time (or after a journey reset). Guards for OTHER pages are left
        // intact — they prevent re-firing on reload of those pages.
        // Keys follow the pattern used in InternalRulesA/B and UdhyamFetch:
        //   internalRulesA_fired_<pageId>
        //   internalRulesB_fired_<pageId>
        //   udhyamFetch_fired_<pageId>
        const autoFireKeys = [
            `internalRulesA_fired_${config.id}`,
            `internalRulesB_fired_${config.id}`,
            `udhyamFetch_fired_${config.id}`,
        ];
        autoFireKeys.forEach(k => sessionStorage.removeItem(k));
        
        // Scroll to top when page loads (always, or if layout.scroll_to_top is true)
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Collect unique getter IDs (deduplicated) for all components on this page
        const getterSet = new Set<string>();
        config.components.forEach(id => {
            const getter = resolveRegistryEntry(id)?.metadata.api?.getter;
            if (getter) getterSet.add(getter);
        });
        const uniqueGetterIds = Array.from(getterSet);
        
        if (uniqueGetterIds.length > 0) {
            const batchData = await orchestratorBatch(uniqueGetterIds);
            
            const initialLocalState: Record<string, any> = {};
            config.components.forEach(compId => {
                const getter = resolveRegistryEntry(compId)?.metadata.api?.getter;
                if (!getter) return;

                const response = batchData[getter];
                if (!response) return;

                const payload =
                    response.status === 'success' && response.data !== undefined
                        ? response.data
                        : response.status === undefined
                            ? response
                            : null;

                if (payload) {
                    initialLocalState[compId] = {
                        ...(initialLocalState[compId] || {}),
                        ...payload,
                    };
                }
            });
            setLocalState(initialLocalState);
        } else {
            setLocalState({});
        }

        // Seed localState from layout config for components that have no API getter
        // (e.g. LOS_TextBlock_100 stores its heading/content in PageConfig.layout).
        // Component IDs may be suffix-keyed (e.g. "LOS_TextBlock_100__0") so we use
        // resolveRegistryEntry to look up the registry entry.
        const layoutOverrides: Record<string, any> = {};
        config.components.forEach(compId => {
            const hasGetter = !!resolveRegistryEntry(compId)?.metadata.api?.getter;
            if (!hasGetter && config.layout && config.layout[compId]) {
                layoutOverrides[compId] = config.layout[compId];
            }
        });
        if (Object.keys(layoutOverrides).length > 0) {
            setLocalState(prev => ({ ...prev, ...layoutOverrides }));
        }
    }, []); // no deps — uses only stable setters and external imports

    // Fetch current journey page from backend state.
    // Stable identity via useCallback with empty deps — navigate accessed via ref.
    const fetchCurrentPage = useCallback(async () => {
        // Prevent re-entrant / concurrent calls (e.g. onDone firing while still loading)
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;

        // Only show the full-screen spinner on the very first load (no page yet).
        // When called from onDone (page already showing), skip setIsLoading(true) so
        // auto-fire components (e.g. NiyoginAAStatus) are NOT unmounted/remounted.
        if (currentPageIdRef.current === null) {
            setIsLoading(true);
        }
        setError(null);

        try {
            const state = await getJourneyState();
            
            // Extract BAP name for conditional header rendering
            if (state?.bap_name) {
                setBapName(state.bap_name);
            }
            
            // The backend returns current_stage which is a page ID (numeric string)
            // It also returns expected_stage (most reliable) and current_checkpoint_index
            const stageId = state.current_stage;
            const expectedStage = state.expected_stage;
            const isAuthenticated = state.is_authenticated;
            
            if (!stageId && !expectedStage) {
                setError("No active journey stage found.");
                return;
            }

            // Handle terminal states first
            if (stageId === 'COMPLETED') {
                setError(null);
                setPageConfig(null);
                navigateRef.current('/loan-success', { replace: true });
                return;
            }
            if (stageId === 'REJECTED') {
                navigateRef.current('/final-reject-state', { replace: true });
                return;
            }

            // Derive journey step progress using progress_page_ids (excludes observer pages
            // with count_in_progress: false — auto-fire verifications, polling pages, etc.).
            // Falls back to full checkpoint_template length if progress_page_ids is absent.
            const template = state.checkpoint_template || [];
            const progressPageIds: string[] = state.progress_page_ids || [];
            const currentIdx = typeof state.current_checkpoint_index === 'number'
                ? state.current_checkpoint_index
                : 0;

            // Determine the current page ID from expected_stage or current_stage
            const currentPageIdStr = String(expectedStage || stageId || '');

            // Grouped section bar. Rendered instead of the flat bar when present;
            // JourneySectionProgress derives the active section from the page ID.
            setProgressSections(Array.isArray(state.progress_sections) ? state.progress_sections : []);

            if (progressPageIds.length > 0) {
                // Use filtered list: total = number of pages that count in progress bar
                const totalStepsCount = progressPageIds.length;
                // Current step = position of current page in the filtered list (1-based),
                // or the last counted step if the current page is an observer page.
                const progressIdx = progressPageIds.indexOf(currentPageIdStr);
                let currentStepNum: number;
                if (progressIdx >= 0) {
                    // Current page counts — use its 1-based position
                    currentStepNum = progressIdx + 1;
                } else {
                    // Current page is an observer page (count_in_progress: false).
                    // Show the step of the last counted page the user passed through.
                    // Find the highest-ranked progress page that comes before the current
                    // page in the full template order.
                    const allIds = template.flat().map(String);
                    const currentPosInFull = allIds.indexOf(currentPageIdStr);
                    let lastCountedIdx = 0;
                    for (let i = 0; i < progressPageIds.length; i++) {
                        const posInFull = allIds.indexOf(progressPageIds[i]);
                        if (posInFull <= currentPosInFull) {
                            lastCountedIdx = i + 1; // 1-based
                        }
                    }
                    currentStepNum = Math.max(1, lastCountedIdx);
                }
                setJourneyTotalSteps(totalStepsCount);
                setJourneyCurrentStep(Math.min(currentStepNum, totalStepsCount));
            } else {
                // Fallback: no progress_page_ids from backend — use full template length
                const totalStepsCount = template.length > 0 ? template.length : 1;
                const currentStepNum = Math.min(currentIdx + 1, totalStepsCount);
                setJourneyTotalSteps(totalStepsCount);
                setJourneyCurrentStep(currentStepNum);
            }

            // Prefer expected_stage from backend (accounts for admin overrides)
            const resolvedStage = expectedStage || stageId;

            // If resolved stage is numeric, fetch the page config directly
            if (/^\d+$/.test(String(resolvedStage))) {
                const newPageId = Number(resolvedStage);

                // Guard: if backend returned the same page we're already showing,
                // do NOT re-load it — that would re-mount auto-fire components
                // (e.g. NiyoginAAStatus) and trigger onDone() again in a loop.
                // Use ref (not state) so the stable closure sees the latest value.
                if (newPageId === currentPageIdRef.current) {
                    return;
                }

                try {
                    const config = await getPageConfig(newPageId);
                    
                    // AUTO-SKIP LOGIC: If user is authenticated and lands on a login page,
                    // automatically advance to the next page in their journey.
                    // This handles the case where a logged-in user refreshes or returns to the login page.
                    const isLoginPage = config.name?.toLowerCase().includes('login');
                    if (isAuthenticated && isLoginPage && currentPageIdRef.current === null) {
                        console.log('[JourneyPage] User is authenticated on login page - auto-advancing to next page');
                        try {
                            await logJourneyEvent({
                                stage: String(newPageId),
                                advance: true,
                                route: '/loan-application'
                            });
                            // Re-fetch to load the next page
                            isFetchingRef.current = false;
                            await fetchCurrentPage();
                            return;
                        } catch (advanceErr) {
                            console.error('[JourneyPage] Failed to auto-advance from login page:', advanceErr);
                            // Fall through to load login page if advance fails
                        }
                    }
                    
                    await loadPageConfig(config);
                } catch (pageErr: any) {
                    console.error(`Failed to load page config for page ID ${newPageId}:`, pageErr);
                    setError(`Page ${newPageId} not found. Your journey may need to be reset. Please contact support.`);
                    return;
                }
            } else {
                // Legacy stage name — try to resolve from checkpoint_template using current_checkpoint_index
                const template = state.checkpoint_template || [];
                const currentIdx = typeof state.current_checkpoint_index === 'number' ? state.current_checkpoint_index : 0;
                if (template.length > 0 && currentIdx < template.length) {
                    const currentCheckpoint = template[currentIdx];
                    if (currentCheckpoint && currentCheckpoint.length > 0) {
                        const pageIdStr = currentCheckpoint[0];
                        if (/^\d+$/.test(pageIdStr)) {
                            try {
                                const config = await getPageConfig(Number(pageIdStr));
                                await loadPageConfig(config);
                                return;
                            } catch (pageErr: any) {
                                console.error(`Failed to load page config for page ID ${pageIdStr}:`, pageErr);
                                setError(`Page ${pageIdStr} not found. Your journey may need to be reset. Please contact support.`);
                                return;
                            }
                        }
                    }
                }
                setError(`Unknown journey stage: ${resolvedStage}. Please contact support.`);
            }
        } catch (err) {
            console.error("Failed to load journey page:", err);
            setError("Failed to load the journey. Please try again.");
        } finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    }, [loadPageConfig]); // loadPageConfig is stable (empty deps), so fetchCurrentPage is also stable

    // Initial load — runs exactly once on mount
    useEffect(() => {
        fetchCurrentPage();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Clear the mandate-save error banner once the user has successfully
    // re-registered the mandate that failed. Watching localState rather than
    // clearing it inside the mandate component's callback keeps the mandate
    // components unaware of this page-level error state.
    useEffect(() => {
        if (!mandateSaveError?.componentId || mandateSaveError.unlocking) return;
        if (localState[mandateSaveError.componentId]?.completed === true) {
            console.log('[JourneyPage] Mandate re-registered — clearing save-error banner.');
            setMandateSaveError(null);
        }
    }, [localState, mandateSaveError]);

    const handleComponentChange = (componentId: string, value: any) => {
        setLocalState(prev => ({
            ...prev,
            [componentId]: {
                ...(prev[componentId] || {}),
                ...value,
            },
        }));
        // Clear gate field errors for any field the user just edited
        if (value && typeof value === 'object' && Object.keys(value).length > 0) {
            setGateFieldErrors(prev => {
                const next = { ...prev };
                Object.keys(value).forEach(k => { delete next[k]; });
                return next;
            });
        }
    };

    const handleNext = async () => {
        if (!pageConfig) return;
        
        setIsSaving(true);
        try {
            // 0. Execute any custom submit handlers registered by components
            const handlers = Object.values(submitHandlersRef.current).filter(Boolean);
            for (const handler of handlers) {
                const success = await handler();
                if (!success) {
                    setIsSaving(false);
                    return;
                }
            }

            // 1. Prepare batch save request
            const setterPayloads: Record<string, any> = {};
            const setterOrder: string[] = [];

            console.log('[JourneyPage] 🔍 DEBUG localState at handleNext:', JSON.stringify(localState, null, 2));

            pageConfig.components.forEach(compId => {
                const entry = resolveRegistryEntry(compId);
                const setter = entry?.metadata.api?.setter;
                if (!setter) return;

                if (!setterOrder.includes(setter)) setterOrder.push(setter);

                const compState = localState[compId] || {};
                const allowedFields = entry?.metadata.fields;

                const rawState = allowedFields
                    ? Object.fromEntries(
                        allowedFields
                            .filter(f => f in compState)
                            .map(f => [f, compState[f]])
                      )
                    : compState;
                const filteredState = Object.fromEntries(
                    Object.entries(rawState).filter(([, v]) => v !== null && v !== undefined && v !== '')
                );

                console.log(`[JourneyPage] 🔍 DEBUG compId=${compId} setter=${setter} compState=`, JSON.stringify(compState), 'filteredState=', JSON.stringify(filteredState));

                setterPayloads[setter] = {
                    ...(setterPayloads[setter] || {}),
                    ...filteredState,
                };
            });

            const savePayload: Record<string, any> = {
                components: setterOrder,
                ...setterPayloads,
            };

            console.log('[JourneyPage] 🔍 DEBUG savePayload to orchestrator:', JSON.stringify(savePayload, null, 2));
            
            // 2. Execute batch save via orchestrator
            if (setterOrder.length > 0) {
                await orchestratorBatch(setterOrder, savePayload);
            }
            
            // 2.5. Call page-specific API endpoint if configured (e.g., Caprion progressive save)
            // This allows pages to execute additional logic after orchestrator saves data
            if (pageConfig.layout && pageConfig.layout.api_endpoint) {
                const apiEndpoint = pageConfig.layout.api_endpoint;
                console.log(`[JourneyPage] Calling page-specific API: ${apiEndpoint}`);
                
                try {
                    // For init-session and similar APIs, don't send stale frontend data
                    // These APIs should fetch fresh data from backend instead
                    const pageData: Record<string, any> = {};
                    
                    // Only collect component data if NOT init-session (to prevent stale data issues)
                    if (!apiEndpoint.includes('init-session')) {
                        pageConfig.components.forEach(compId => {
                            const compState = localState[compId] || {};
                            Object.assign(pageData, compState);
                        });
                    }
                    
                    const response = await axios.post(`${API_BASE_URL}${apiEndpoint}`, pageData, {
                        withCredentials: true,
                        headers: {
                            'Content-Type': 'application/json',
                        }
                    });
                    
                    console.log(`[JourneyPage] Page API response:`, response.data);
                    
                    // Check if this API requires polling (Caprion create-lead-from-db)
                    if (response.data && response.data.should_poll && response.data.application_id) {
                        console.log(`[JourneyPage] API requires polling - starting status check for application ${response.data.application_id}`);
                        
                        // Start polling and wait for completion
                        const pollingSuccess = await startPolling({
                            applicationId: response.data.application_id,
                            onSuccess: (data) => {
                                console.log('[JourneyPage] Polling completed successfully:', data);
                                // No toast notification - user will see next page directly
                            },
                            onError: (error) => {
                                console.error('[JourneyPage] Polling failed:', error);
                                toast({
                                    variant: "destructive",
                                    title: "Status Check Failed",
                                    description: error,
                                });
                            }
                        });
                        
                        // If polling failed, don't proceed with journey advancement
                        if (!pollingSuccess) {
                            setIsSaving(false);
                            return;
                        }
                    } else if (response.data && response.data.redirect_to_curating_offers) {
                        // Non-sole proprietorship detected - redirect to curating offers page
                        console.log(`[JourneyPage] Redirecting to curating offers - business type: ${response.data.data?.business_constitution_type}`);
                        toast({
                            title: "Redirecting...",
                            description: "Taking you to our curating offers page",
                        });
                        setIsSaving(false);
                        navigate('/whitespace/curating-offers', { replace: true });
                        return;
                    } else if (response.data && !response.data.success) {
                        // If the API call failed, show error but don't block journey progression
                        console.warn(`[JourneyPage] Page API returned error:`, response.data.error || response.data.message);
                        toast({
                            variant: "destructive",
                            title: "Warning",
                            description: response.data.error || response.data.message || "Additional processing failed, but your data was saved.",
                        });
                    }
                } catch (apiError: any) {
                    console.error(`[JourneyPage] Page API call failed:`, apiError);
                    // Don't block journey progression if page-specific API fails
                    toast({
                        variant: "destructive",
                        title: "Warning",
                        description: apiError.response?.data?.error || "Additional processing failed, but your data was saved.",
                    });
                }
            }
            
            // 2.75. SOLE PROPRIETOR PRE-FILL: If leaving Personal Info page, check for Udyam data
            // This calls PAN-to-Udyam + PAN-to-GST APIs and saves business data to DB BEFORE navigation
            if (pageConfig.layout?.journey_key === 'PERSONAL_INFO') {
                console.log('[JourneyPage] Leaving Personal Info page - checking for sole proprietor...');
                try {
                    const response = await axios.post(
                        `${API_BASE_URL}/api/caprion/check-personal-pan-udyam/`,
                        {},
                        {
                            withCredentials: true,
                            headers: {
                                'Content-Type': 'application/json',
                            }
                        }
                    );
                    
                    console.log('[JourneyPage] PAN check response:', response.data);
                    
                    if (response.data.success && response.data.is_sole_proprietor) {
                        console.log('[JourneyPage] ✅ Sole proprietor detected - business data saved to DB');
                        toast({
                            title: "Business details auto-filled",
                            description: "Your business information has been pre-filled from Udyam",
                        });
                    } else {
                        console.log('[JourneyPage] Not a sole proprietor or no Udyam found');
                    }
                } catch (panError: any) {
                    console.error('[JourneyPage] Error checking personal PAN:', panError);
                    // Don't block navigation on error - user can still proceed
                }
            }

            // 2.78. LOCATION SERVICEABILITY: On Continue from the Business Info page,
            // check that at least ONE of the applicant's pincodes (personal
            // communication/residential or business/registered) is in the WhitelistPincode
            // table. Runs here — not on the Ruleset A page — because Ruleset A is
            // non-blocking and shows only a spinner, whereas here the user can still fix
            // the address. Blocking: on failure we stay on this page and show the error.
            if (pageConfig.layout?.journey_key === 'BUSINESS_INFO') {
                try {
                    const locRes = await axios.post(
                        `${API_BASE_URL}/api/caprion/verify/location-serviceable/`,
                        {},
                        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
                    );
                    console.log('[JourneyPage] Location serviceability:', locRes.data?.status, locRes.data?.message);
                    if (locRes.data?.status === 'failed') {
                        toast({
                            variant: 'destructive',
                            title: 'Location not serviceable',
                            description: locRes.data?.message
                                || 'We are currently not servicing this location.',
                        });
                        setIsSaving(false);
                        return; // Block journey advance — user stays on Business Info
                    }
                } catch (locErr: any) {
                    // A serviceability check that cannot run must not silently let an
                    // out-of-area applicant through, so this failure is blocking too.
                    console.error('[JourneyPage] Location serviceability check failed:', locErr);
                    toast({
                        variant: 'destructive',
                        title: 'Could not verify your location',
                        description: locErr?.response?.data?.error
                            || 'Please check your pincode and try again.',
                    });
                    setIsSaving(false);
                    return;
                }
            }

            // 2.79. MANDATE → LENDER PUSH: On Continue from the eMandate page, push every
            // registered mandate to the lender (Caprion SaveMandateDetails) — NACH first,
            // then UPI. This used to fire from inside the 4s Digio polling loop, where its
            // failures were swallowed as warnings, so a customer could reach e-sign while
            // the lender held no mandate. It now runs exactly once, here, and is BLOCKING:
            // if either registered mandate cannot be saved, the user stays on this page and
            // sees the error. See backend/los_app/api_views/caprion/mandate_save.py.
            if (pageConfig.layout?.journey_key === 'ENACH_MANDATE') {
                try {
                    const saveRes = await axios.post(
                        `${API_BASE_URL}/api/caprion/mandates/save/`,
                        {},
                        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
                    );
                    console.log('[JourneyPage] Mandate save results:', saveRes.data?.results);
                } catch (saveErr: any) {
                    const data = saveErr?.response?.data;
                    console.error('[JourneyPage] Mandate save to lender failed:', data || saveErr);

                    const message = data?.error
                        || 'We could not register your repayment mandate with the lender. Please try again.';

                    // The backend CANCELS the mandate it could not push to the lender
                    // (a mandate live at the bank that the lender has no record of is
                    // worse than none) and returns retry_mandate:'nach'|'upi'.
                    const retry: string | null = data?.retry_mandate ?? null;
                    const retryBaseId = retry === 'nach'
                        ? 'LOS_NACHMandate_419'
                        : retry === 'upi'
                            ? 'LOS_UPIMandate_418'
                            : null;
                    const retryComponentId = retryBaseId
                        ? (pageConfig.components.find(
                            id => id.replace(/__\d+$/, '') === retryBaseId
                          ) ?? null)
                        : null;

                    // Show the reason as a persistent banner FIRST and hold it there for
                    // a moment before unlocking the card. Unlocking instantly would
                    // re-render the card back to its "Setup Mandate" state in the same
                    // frame as the error appears, which reads as if nothing happened —
                    // the user needs a beat to see WHY they are being asked to redo it.
                    setMandateSaveError({
                        message,
                        retry,
                        componentId: retryComponentId,
                        unlocking: !!retryComponentId,
                    });

                    toast({
                        variant: 'destructive',
                        title: 'Mandate could not be registered',
                        description: message,
                    });

                    if (retryComponentId) {
                        const MANDATE_UNLOCK_DELAY_MS = 2500;
                        setTimeout(() => {
                            console.log(`[JourneyPage] Unlocking ${retry} card for retry:`, retryComponentId);
                            setLocalState(prev => ({
                                ...prev,
                                [retryComponentId]: {
                                    ...(prev[retryComponentId] || {}),
                                    completed: false,
                                    // Bumped so the mandate component remounts and drops
                                    // its internal isCompleted state (it is not derived
                                    // from localState once mounted).
                                    retryToken: Date.now(),
                                },
                            }));
                            // Banner stays (with the reason) but stops saying "unlocking…"
                            setMandateSaveError(prev =>
                                prev ? { ...prev, unlocking: false } : prev
                            );
                        }, MANDATE_UNLOCK_DELAY_MS);
                    }

                    setIsSaving(false);
                    return; // Block journey advance — user stays on the eMandate page
                }
            }

            // 2.795. AADHAAR OKYC → LENDER PUSH: On Continue from the Aadhaar Verification
            // page, push the verified OKYC XML to the lender (Caprion SaveAadhaarDetails).
            // This used to fire from inside the 4s Digio KYC polling loop, where its
            // failures were swallowed as warnings — so a customer whose XML Caprion
            // REJECTED (e.g. code 32, "Name value present in Aadhaar OKyc XML does not
            // match the applicant details present in the system") reached e-sign while
            // the lender held no Aadhaar KYC. It now runs exactly once, here, and is
            // BLOCKING: the user stays on this page and sees a persistent banner.
            // See backend/los_app/api_views/caprion/aadhaar_save.py.
            if (pageConfig.layout?.journey_key === 'SELFIE_AADHAAR_MATCH') {
                try {
                    const aadhaarRes = await axios.post(
                        `${API_BASE_URL}/api/caprion/aadhaar/save/`,
                        {},
                        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
                    );
                    console.log('[JourneyPage] Aadhaar OKYC save result:', aadhaarRes.data?.result);
                    // Succeeded — drop any banner left over from an earlier attempt.
                    setAadhaarSaveError(false);
                } catch (aadhaarErr: any) {
                    const data = aadhaarErr?.response?.data;
                    console.error('[JourneyPage] Aadhaar OKYC save to lender failed:', data || aadhaarErr);

                    // Deliberately NOT surfacing data.error (the lender's raw text,
                    // e.g. "Name value present in Aadhaar OKyc XML does not match the
                    // applicant details present in the system"). It names our vendor
                    // and its internal XML format, and reads as a system fault rather
                    // than something the applicant can act on. The real reason is
                    // already in the logs and in CaprionRequestResponse for support.
                    // The banner shows plain guidance instead.
                    setAadhaarSaveError(true);

                    // No toast here — the persistent banner below IS the message.
                    // Showing both produced two identical red blocks stacked on top
                    // of each other (see the duplicated copy in the bug screenshot).

                    // Bring the banner into view — on this page the Continue button
                    // sits in a fixed footer, so a banner rendered at the top of the
                    // content can be off-screen when the user presses it.
                    window.scrollTo({ top: 0, behavior: 'smooth' });

                    setIsSaving(false);
                    return; // Block journey advance — user stays on the Aadhaar page
                }
            }

            // 2.8. PREFILL GATE: Scan page components for invisible prefillGate entries.
            // Any component with metadata.prefillGate set fires its check URL and blocks
            // advance on 'failed'. This is fully config-driven — add LOS_PrefillGate_Name_V10,
            // LOS_PrefillGate_DOB_V11, or LOS_PrefillGate_PAN_V12 to any builder page to gate it.
            {
                const gateChecks: Array<{ url: string; label: string }> = [];
                pageConfig.components.forEach(compId => {
                    const entry = resolveRegistryEntry(compId);
                    const gate = entry?.metadata?.prefillGate;
                    if (gate) gateChecks.push(gate);
                });

                if (gateChecks.length > 0) {
                    console.log(`[JourneyPage] Running ${gateChecks.length} prefill gate check(s)...`);
                    // Collect all current form values to send to gate endpoints so they
                    // compare against what the user just typed, not stale DB values.
                    const gateFormData: Record<string, any> = {};
                    pageConfig.components.forEach(compId => {
                        const compState = localState[compId] || {};
                        Object.assign(gateFormData, compState);
                    });
                    console.log('[JourneyPage] 🔍 DEBUG gateFormData sent to gate endpoints:', JSON.stringify(gateFormData, null, 2));
                    const failures: string[] = [];
                    const newFieldErrors: Record<string, string> = {};
                    for (const check of gateChecks) {
                        try {
                            const res = await axios.post(
                                `${API_BASE_URL}${check.url}`,
                                gateFormData,
                                { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
                            );
                            // Backend returns { success, status, message, field?, comment, verification_code }
                            const status = res.data?.status;
                            const msg    = res.data?.message || '';
                            const field  = res.data?.field || '';
                            console.log(`[JourneyPage] Prefill gate ${check.label}: ${status} — ${msg}`);
                            if (status === 'failed') {
                                if (field) {
                                    newFieldErrors[field] = msg || `${check.label} does not match our records.`;
                                } else {
                                    failures.push(msg || `${check.label} does not match our records. Please correct it before proceeding.`);
                                }
                            }
                        } catch (checkErr: any) {
                            console.error(`[JourneyPage] Prefill gate ${check.label} error:`, checkErr);
                            const errMsg = checkErr?.response?.data?.error || `Could not verify your ${check.label}. Please try again.`;
                            failures.push(errMsg);
                        }
                    }
                    // Run ALL gates before returning so all field errors are collected
                    // in one pass — not just the first failed gate.
                    if (Object.keys(newFieldErrors).length > 0 || failures.length > 0) {
                        if (Object.keys(newFieldErrors).length > 0) {
                            setGateFieldErrors(newFieldErrors);
                        }
                        if (failures.length > 0) {
                            toast({
                                variant: 'destructive',
                                title: 'Please correct the following',
                                description: failures.join(' · '),
                            });
                        }
                        setIsSaving(false);
                        return; // Block journey advance
                    }
                    // All gates passed — clear any stale field errors
                    setGateFieldErrors({});
                    console.log('[JourneyPage] ✅ All prefill gate checks passed — proceeding.');
                }
            }

            // 3. Advance journey — send current page ID so backend knows where we are
            const advanceResponse = await logJourneyEvent({
                stage: String(pageConfig.id),
                advance: true,
                route: '/loan-application'
            });
            
            // 4. Re-fetch the new current page (URL stays the same)
            if (advanceResponse.is_last_page) {
                toast({
                    title: "Journey Complete",
                    description: "You have completed all steps.",
                });
            }
            
            // Re-load the current page from backend state
            await fetchCurrentPage();
            
        } catch (err) {
            console.error("Failed to save and advance:", err);
            toast({
                variant: "destructive",
                title: "Error",
                description: "Failed to save data. Please try again.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50">
                <Loader2 className="h-12 w-12 animate-spin text-gray-500" />
            </div>
        );
    }

    if (error || !pageConfig) {
        return (
            <div className="flex justify-center items-center h-screen bg-red-50 text-red-600 p-4 text-center">
                {error || "Unknown error loading journey step."}
            </div>
        );
    }

    // Detect if this page is an auto-fire page OR self-advancing page:
    // - autoFire: true → component drives journey via onDone callback
    // - selfAdvances: true → component has its own Continue button
    // In both cases, hide the generic Continue button at the bottom.
    const isAutoFirePage = pageConfig.components.length > 0 &&
        pageConfig.components.some(id => {
            const metadata = resolveRegistryEntry(id)?.metadata;
            return metadata?.autoFire === true || metadata?.selfAdvances === true;
        });

    // Mandate page gate: Continue button is shown but DISABLED until either UPI or NACH
    // is confirmed success (onChange({completed:true}) fires from the component).
    // mandateDone is set true when any mandate component reports completed:true via onChange.
    // On reload it is re-initialised from localState (which is prefilled from backend).
    const isMandatePage = pageConfig.components.length > 0 &&
        pageConfig.components.some(id => {
            const base = id.replace(/__\d+$/, '');
            return base === 'LOS_UPIMandate_418' || base === 'LOS_NACHMandate_419';
        });
    const mandateDone = isMandatePage &&
        pageConfig.components.some(id => {
            return localState[id]?.completed === true;
        });

    // Separate, STRICTER flag for hiding the "Change Bank Details" escape hatch:
    // every mandate component on the page must report completed:true, not just one.
    // mandateDone above is deliberately loose (either UPI or NACH unlocks Continue),
    // but as long as ONE mandate is still outstanding the user may well be stuck on
    // it because of a wrong account number / IFSC — which is exactly when they need
    // the escape hatch. So it stays visible until nothing is left to set up.
    const allMandatesDone = isMandatePage &&
        pageConfig.components
            .filter(id => {
                const base = id.replace(/__\d+$/, '');
                return base === 'LOS_UPIMandate_418' || base === 'LOS_NACHMandate_419';
            })
            .every(id => localState[id]?.completed === true);

    // Required-field gate (config-driven): PageConfig.layout.required_fields lists the
    // field names that must be filled before Continue is enabled. Until then the button
    // stays disabled (grey); once every listed field has a value it turns green.
    // Fields are read from the merged component state, so it does not matter which
    // component owns a field. Pages without required_fields behave as before.
    const requiredFields: string[] = Array.isArray(pageConfig.layout?.required_fields)
        ? pageConfig.layout.required_fields
        : [];
    // Merge order matters: later components overwrite earlier ones. Components whose
    // registry entry declares a `fields` allowlist contribute ONLY those fields here.
    // Without this, a component with a broad getter clobbers fields it does not own:
    // LOS_HHICheckboxGate_V25 sits last on the Personal Info page and its getter
    // (LOS_GetIndividualDetails_001) returns the whole flattened IndividualDetail,
    // including communication_address_* as "" when no address row exists yet — which
    // overwrote the address the user had just typed into the composite address
    // component and left Continue permanently disabled. The allowlist already exists
    // on the metadata for exactly this reason (it scopes the setter payload); applying
    // it to the gate merge keeps both sides consistent.
    const mergedFieldValues: Record<string, any> = {};
    pageConfig.components.forEach(compId => {
        const compState = localState[compId] || {};
        const allowed = resolveRegistryEntry(compId)?.metadata?.fields;
        if (Array.isArray(allowed) && allowed.length > 0) {
            allowed.forEach(field => {
                if (field in compState) mergedFieldValues[field] = compState[field];
            });
        } else {
            Object.assign(mergedFieldValues, compState);
        }
    });
    const isFieldFilled = (field: string) => {
        const val = mergedFieldValues[field];
        // Checkbox-style gates (e.g. HHI declaration) must be explicitly true.
        if (typeof val === 'boolean') return val === true;
        return val !== null && val !== undefined && String(val).trim() !== '';
    };
    const missingRequiredFields = requiredFields.filter(f => !isFieldFilled(f));
    const requiredFieldsFilled = missingRequiredFields.length === 0;
    if (!requiredFieldsFilled) {
        // Logged so a permanently-disabled Continue button is trivial to diagnose:
        // a field listed in layout.required_fields that no component on the page
        // ever writes to state will show up here.
        console.log('[JourneyPage] Continue disabled — missing required fields:', missingRequiredFields);
    }

    // Detect if any component on this page renders its own full-page layout
    // (min-h-screen + JourneyHeader). When true, JourneyPage must suppress its
    // own sticky header so the two headers don't stack and the progress bar doesn't
    // appear twice. The component is responsible for its own chrome.
    const hasFullPageComponent = pageConfig.components.length > 0 &&
        pageConfig.components.some(id => {
            const metadata = resolveRegistryEntry(id)?.metadata;
            return metadata?.fullPageComponent === true;
        });

    // Check if this is a login page that should show branded header
    const showBrandedHeader = pageConfig.name?.toLowerCase().includes('login') &&
        (bapName === 'PhonePe' || bapName === 'Caprion' || bapName === 'PetPooja');

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center">
            {/* Caprion Status Polling Loader Overlay */}
            {isPolling && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
                        {/* Loader drives its own step sequence internally — it has no
                            `currentStep` prop (passing one was a stale leftover that
                            broke the type-check build). */}
                        <CaprionStatusLoader />
                    </div>
                </div>
            )}
            
            {/* Branded Header — shown for login pages of specific BAPs */}
            {showBrandedHeader && !hasFullPageComponent && (
                <BrandedJourneyHeader className="w-full" />
            )}
            
            {/* Standard Header with progress bar — hidden only when a component owns its
                own full-page layout (fullPageComponent:true) or when it's a branded login.
                autoFire pages that do NOT own their full layout still get the progress bar. */}
            {!hasFullPageComponent && !showBrandedHeader && !isAutoFirePage && (
                <div className="w-full max-w-md bg-white border-b px-4 py-3 sticky top-0 z-50 shadow-sm">
                    {/* Header with title */}
                    <div className="flex items-center gap-3 mb-2">
                        <h1 className="text-lg font-bold text-gray-800 truncate">{pageConfig.name}</h1>
                    </div>

                    {/* Grouped section progress bar when the journey's pages declare
                        layout.progress_section; otherwise the flat per-page bar. */}
                    {progressSections.length > 0 ? (
                        <JourneySectionProgress
                            sections={progressSections}
                            currentPageId={currentPageId}
                        />
                    ) : (
                        <SegmentedProgressBar
                            currentStep={journeyCurrentStep}
                            totalSteps={journeyTotalSteps}
                            isBuilderMode={false}
                        />
                    )}
                </div>
            )}

            {/* Content Area — pb-32 reserves space for the fixed footer Continue button.
                Full-page components (fullPageComponent: true) own the whole viewport:
                they render their own min-h-screen / h-[100dvh] layout and their own
                header. Wrapping them in max-w-md + p-4 inset them inside a grey
                gutter, so their header/progress bar did not sit flush like it does on
                the Personal/Business Information pages. For those, main is a bare
                full-width container. */}
            <main
                className={
                    hasFullPageComponent
                        ? 'w-full flex-1'
                        : `w-full max-w-md flex-1 p-4 space-y-6 ${isAutoFirePage ? '' : 'pb-32'}`
                }
            >
                {/* Lender-side mandate registration failure. Persistent (unlike the toast)
                    because the user has to redo the mandate and needs the reason to stay
                    on screen while that happens. Cleared once they successfully re-register
                    the affected mandate — see the effect that watches localState below. */}
                {mandateSaveError && (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="flex gap-3">
                            <AlertCircle className="h-5 w-5 shrink-0 text-red-600" />
                            <div className="flex-1">
                                <p className="text-sm font-bold text-red-900">
                                    Mandate could not be registered
                                </p>
                                <p className="mt-1 text-sm text-red-800">
                                    {mandateSaveError.message}
                                </p>
                                {mandateSaveError.retry && (
                                    <p className="mt-2 flex items-center gap-2 text-xs font-semibold text-red-700">
                                        {mandateSaveError.unlocking ? (
                                            <>
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                Cancelling that mandate so you can set it up again…
                                            </>
                                        ) : (
                                            <>
                                                Your {mandateSaveError.retry.toUpperCase()} mandate was
                                                cancelled. Please set it up again below, or use
                                                “Change Bank Details” if your account details are wrong.
                                            </>
                                        )}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Lender-side Aadhaar OKYC (SaveAadhaarDetails) failure. No toast is
                    shown alongside this — the banner IS the message, and rendering both
                    produced two identical stacked red blocks. The lender's raw wording
                    is deliberately omitted (see the aadhaarSaveError declaration); this
                    copy explains the likely cause in the applicant's own terms and
                    points at support, because the usual cause is a name/DOB mismatch
                    that pressing Continue again cannot fix. */}
                {aadhaarSaveError && (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="flex gap-3">
                            <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
                            <div className="flex-1">
                                <p className="text-sm font-bold text-red-900">
                                    Aadhaar verification could not be completed
                                </p>
                                <p className="mt-1 text-sm text-red-800">
                                    The name or date of birth on your Aadhaar does not match
                                    the details on your application. Pressing Continue again
                                    will not resolve this — please contact our support team.
                                </p>
                                <Button
                                    onClick={() => setAadhaarSupportOpen(true)}
                                    className="mt-3 w-full bg-intellend-green hover:bg-intellend-green/90 text-white flex items-center justify-center gap-2"
                                >
                                    <PhoneCall className="h-4 w-4" />
                                    Contact Support
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                <CallbackHelp
                    isOpen={aadhaarSupportOpen}
                    setIsOpen={setAadhaarSupportOpen}
                    showOk={false}
                    showCallUs
                    showWhatsappUs
                    silentlyTrigger
                    title="Need help with your Aadhaar details?"
                    description="Our team can help correct the mismatch between your Aadhaar and your application details. Call or WhatsApp us and we'll take it from there."
                />

                {pageConfig.components.map((componentId, index) => {
                    const registryEntry = resolveRegistryEntry(componentId);
                    if (!registryEntry) {
                        console.warn(`[JourneyPage] Unknown component ID: "${componentId}". Skipping.`);
                        return null;
                    }

                    // On a true auto-fire page (autoFire:true), skip non-autoFire, non-selfAdvances
                    // components that are admin/system-only (e.g. JourneyAnalysis, CreditScoreCheck).
                    // These were accidentally added to the page in the builder and
                    // would cause 403 errors when rendered in the user journey.
                    // selfAdvances components always render — they are user-facing.
                    if (isAutoFirePage && !registryEntry.metadata.autoFire && !registryEntry.metadata.selfAdvances) {
                        console.warn(`[JourneyPage] Skipping non-autoFire component "${componentId}" on auto-fire page.`);
                        return null;
                    }

                    const Component = registryEntry.component;
                    const value = localState[componentId] ?? registryEntry.metadata.validation?.defaultValues ?? {};

                    // Extra props for specific components
                    const baseId = componentId.replace(/__\d+$/, '');

                    // Escape hatch for the eMandate page, rendered just ABOVE the first
                    // mandate card (i.e. after the page header, before UPI). A mandate can
                    // never register against a wrong account number / IFSC, and Continue
                    // here stays disabled until one reaches status=4 — so this is the only
                    // way out. It must sit above BOTH cards because the NACH card is locked
                    // (pointer-events-none) until UPI completes or is skipped, which would
                    // otherwise bury the button exactly when it is most needed.
                    //
                    // Gated on allMandatesDone, NOT mandateDone: while even one mandate is
                    // still outstanding the user may be stuck on it with wrong bank details,
                    // so the hatch must stay. It disappears only once both are registered.
                    // The backend cancels every existing mandate at Digio before rewinding.
                    const showChangeBankDetails =
                        isMandatePage && !allMandatesDone && baseId === 'LOS_UPIMandate_418';
                    const extraProps: Record<string, any> = {};
                    if (baseId === 'LOS_ProgressBar_102') {
                        extraProps.currentStep = journeyCurrentStep;
                        extraProps.totalSteps = journeyTotalSteps;
                    }

                    // Full-page components own their own layout (header + progress bar).
                    // Pass currentStep/totalSteps so they can render the progress bar themselves,
                    // plus the section list + page id so they can render the grouped bar instead.
                    if (registryEntry.metadata.fullPageComponent) {
                        extraProps.currentStep = journeyCurrentStep;
                        extraProps.totalSteps = journeyTotalSteps;
                        extraProps.progressSections = progressSections;
                        extraProps.currentPageId = currentPageId;
                    }

                    // Both-mandate wiring (UPI + NACH both required):
                    // UPIMandate notifies us when UPI is completed/skipped → we set upiSkipped=true
                    // NACHMandate receives isUpiCompleted so it unlocks after UPI.
                    //
                    // NEITHER component advances the journey. Completing a mandate only
                    // enables the footer Continue button (via onChange({completed:true}) →
                    // mandateDone). The ONLY thing that moves the user past this page is
                    // the user clicking Continue → handleNext() → logJourneyEvent(advance).
                    // Previously NACH's onDone called logJourneyEvent({advance:true})
                    // directly, so the recorded stage jumped forward the moment the
                    // backend confirmed the mandate — which is why a page refresh after a
                    // successful NACH silently skipped ahead (and why UPI-only did not,
                    // since UPI's onDone never advanced).
                    if (baseId === 'LOS_UPIMandate_418') {
                        // When UPI completes (success or skip), unlock NACH but DON'T advance journey.
                        // onUpiSkipped is called by the skip button path; onDone is called by success path.
                        // Both just set upiSkipped=true so NACHMandate unlocks.
                        extraProps.onUpiSkipped = () => setUpiSkipped(true);
                        extraProps.onDone = () => {
                            // UPI success → unlock NACH, don't advance journey yet
                            setUpiSkipped(true);
                        };
                    }
                    if (baseId === 'LOS_NACHMandate_419') {
                        // NACH unlocks when UPI is done (success or skip)
                        extraProps.isUpiCompleted = upiSkipped;
                        // NACH confirmed by the backend → do NOT advance. The component
                        // has already fired onChange({completed:true}), which flips
                        // mandateDone and turns the Continue button green. Nothing to do
                        // here; deliberately no logJourneyEvent and no fetchCurrentPage
                        // (a re-fetch would re-mount the mandate components mid-flow).
                        extraProps.onDone = () => {
                            console.log('[JourneyPage] Mandate confirmed — Continue enabled. Journey advances only on Continue.');
                        };
                    }
                    
                    // PAN-to-Udyam and PAN-to-GST auto-fill: When CompanyPANInput enriches data
                    if (baseId === 'LOS_CompanyPANInput_068') {
                        // Handle GST enrichment
                        extraProps.onGSTEnriched = (gstNumber: string) => {
                            // Find the GST component ID on this page
                            const gstComponentId = pageConfig.components.find(id =>
                                id.replace(/__\d+$/, '') === 'LOS_GstInput_025'
                            );
                            if (gstComponentId) {
                                handleComponentChange(gstComponentId, { gst_number: gstNumber });
                            }
                        };
                        
                        // Handle business data enrichment from Udyam
                        extraProps.onBusinessDataEnriched = (businessData: any) => {
                            // Map Udyam data to component IDs and update them
                            const fieldMapping: Record<string, string> = {
                                business_name: 'LOS_BusinessNameInput_067',
                                date_of_incorporation: 'LOS_DateOfIncorporationInput_069',
                                business_type: 'LOS_BusinessTypeInput_070',
                                business_address: 'LOS_BusinessAddressInput_071',
                                business_city: 'LOS_BusinessCityInput_072',
                                business_state: 'LOS_BusinessStateInput_073',
                                business_pincode: 'LOS_BusinessPincodeInput_074',
                            };
                            
                            // Update each field if data exists
                            Object.entries(fieldMapping).forEach(([fieldName, baseComponentId]) => {
                                if (businessData[fieldName]) {
                                    // Find the actual component ID on this page (may have __N suffix)
                                    const componentId = pageConfig.components.find(id =>
                                        id.replace(/__\d+$/, '') === baseComponentId
                                    );
                                    if (componentId) {
                                        handleComponentChange(componentId, { [fieldName]: businessData[fieldName] });
                                    }
                                }
                            });
                        };
                    }

                    if (registryEntry.metadata.autoFire) {
                        // Auto-fire component: render without wrapper, pass onDone callback.
                        // NOTE: Some auto-fire components (e.g. InternalRulesA/B) already call
                        // advance_to_next_stage on the backend themselves. For those, onDone
                        // should just re-fetch journey state without double-advancing.
                        // Components that do NOT advance the backend themselves should set
                        // metadata.selfAdvances = false (default) so we call logJourneyEvent here.
                        const selfAdvances = registryEntry.metadata.selfAdvances !== false
                            ? !!registryEntry.metadata.selfAdvances
                            : false;
                        const handleAutoFireDone = async () => {
                            if (!selfAdvances) {
                                try {
                                    await logJourneyEvent({
                                        stage: String(pageConfig.id),
                                        advance: true,
                                        route: '/loan-application',
                                    });
                                } catch (err) {
                                    console.error('[JourneyPage] Failed to advance journey from auto-fire component:', err);
                                }
                            }
                            await fetchCurrentPage();
                        };
                        return (
                            <Component
                                key={`${componentId}-${index}-${pageConfig.id}`}
                                value={value}
                                onChange={(val: any) => handleComponentChange(componentId, val)}
                                disabled={isSaving}
                                onDone={handleAutoFireDone}
                                onNext={handleNext}
                                registerSubmit={(handler: () => Promise<boolean>) => {
                                    submitHandlersRef.current[componentId] = handler;
                                }}
                                pageId={pageConfig.id}
                                {...extraProps}
                            />
                        );
                    }

                    // Invisible gate components (invisible: true) render nothing — skip the
                    // wrapper card entirely so they don't take up whitespace on the page.
                    if (registryEntry.metadata.invisible) {
                        return (
                            <Component
                                key={`${componentId}-${index}`}
                                value={value}
                                onChange={(val: any) => handleComponentChange(componentId, val)}
                                disabled={isSaving}
                                onNext={handleNext}
                                registerSubmit={(handler: () => Promise<boolean>) => {
                                    submitHandlersRef.current[componentId] = handler;
                                }}
                                fieldErrors={gateFieldErrors}
                                {...extraProps}
                            />
                        );
                    }

                    // retryToken is bumped when a mandate save failed and the backend
                    // cancelled that mandate — including it in the key remounts the card
                    // so its internal isCompleted state resets (that state is local to the
                    // component, not derived from localState, so a prop change alone would
                    // leave it stuck showing "Registered").
                    const retryToken = localState[componentId]?.retryToken ?? '';
                    return (
                        <React.Fragment key={`${componentId}-${index}-${retryToken}`}>
                        {showChangeBankDetails && <ChangeBankDetailsLink disabled={isSaving} />}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                             <Component
                                value={value}
                                onChange={(val: any) => handleComponentChange(componentId, val)}
                                disabled={isSaving}
                                onNext={handleNext}
                                registerSubmit={(handler: () => Promise<boolean>) => {
                                    submitHandlersRef.current[componentId] = handler;
                                }}
                                fieldErrors={gateFieldErrors}
                                {...extraProps}
                             />
                        </div>
                        </React.Fragment>
                    );
                })}
            </main>

            {/* Footer Navigation — hidden for auto-fire pages.
                The button is grey/disabled until the page is ready to advance, then green:
                - while saving,
                - on mandate pages until either UPI or NACH is confirmed success
                  (mandateDone=true; re-derived from prefilled localState on reload),
                - until every layout.required_fields entry has a value. */}
            {/* data-bottom-chrome: hidden while a Digio overlay is open. The overlay stops
                ~72px above the bottom of the screen so the gateway's own action row clears
                the mobile browser chrome — this fixed footer showed through that gap and
                covered the gateway's Submit button on mobile. Raising the overlay z-index
                cannot help: the footer sits IN the gap, not behind the overlay.
                See DIGIO_OVERLAY_BODY_ATTRIBUTE in hooks/useDigioSdk.ts + index.css. */}
            {!isAutoFirePage && (
                <div data-bottom-chrome className="w-full max-w-md bg-white border-t p-4 fixed bottom-0 z-10 shadow-[0_-4px_10px_-1px_rgba(0,0,0,0.05)]">
                    <button
                        onClick={handleNext}
                        disabled={isSaving || (isMandatePage && !mandateDone) || !requiredFieldsFilled}
                        className="w-full bg-intellend-green text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-intellend-green/90 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed disabled:hover:bg-gray-300 disabled:active:scale-100 transition-all active:scale-95"
                    >
                        {isSaving ? (
                            <>
                                <Loader2 className="h-5 w-5 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                {pageConfig?.layout?.continue_button_text || 'Continue'}
                                <ArrowRight className="h-5 w-5" />
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
}
